import { ReactNode, useEffect, useRef, useState } from "react";

import {
  ITmpOnboardingLink,
  ITmpSession,
  ITmpSessionDetail,
} from "../../../@types/admin/AdminTmpIntegration";
import {
  useIssueTmpOnboardingLink,
  useRetryTmpSession,
} from "../../../hooks/mutations/admin/tmpIntegration";
import {
  useTmpSessionDetail,
  useTmpSessionEvents,
} from "../../../hooks/queries/admin/tmpIntegration";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  ErrorMessage,
  FilterButton,
  ModalContainer,
  ModalFormActions,
  ModalHeader,
  ModalLabel,
  ModalSubtitle,
  ModalTitle,
  ModalValue,
  StatusBadge,
  TableLoadingState,
} from "../shared";
import { MessageErrorNotice } from "./AdminJupyterIntegration.styles";
import {
  Collapsible,
  CopyLinkButton,
  DetailGrid,
  DetailSectionTitle,
  LinkRow,
  LinkValue,
  NextStepText,
  StatusSummary,
} from "./AdminTmpIntegration.styles";
import {
  fieldList,
  nextStep,
  provisioningStatusLabel,
  provisioningStatusTone,
} from "./tmpSessionPresentation";
import { TmpSessionTimeline } from "./TmpSessionTimeline";

export { provisioningStatusTone } from "./tmpSessionPresentation";

interface ITmpSessionDrillDownModalProps {
  /** The list row — names the modal while the detail loads. */
  session: ITmpSession;
  onClose: () => void;
}

export function patientDisplayName(session: ITmpSession): string {
  const name = [session.patientFirstName, session.patientLastName]
    .filter(Boolean)
    .join(" ");
  return name || `MRN ${session.externalPatientId}`;
}

/** TMP's collections come through as JSON strings; show them as JSON. */
export function formatJsonString(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** A JSON array of strings (emails, phone numbers) as a comma list; empty when there is none. */
export function jsonList(value: string | null | undefined): string {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).join(", ") : "";
  } catch {
    return "";
  }
}

/** An HCP block as "Name · RIZIV"; empty when TMP sent none. SSINs are masked server-side and never shown. */
export function hcpSummary(value: string | null | undefined): string {
  if (!value) return "";
  try {
    const hcp = JSON.parse(value) ?? {};
    const name =
      [hcp.firstName, hcp.lastName].filter(Boolean).join(" ") || hcp.name;
    return [name, hcp.healthProviderId].filter(Boolean).join(" · ");
  } catch {
    return "";
  }
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <ModalLabel>{label}</ModalLabel>
      <ModalValue>{children}</ModalValue>
    </div>
  );
}

function text(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/** The patient data TMP sent that pMp uses. Everything else stays in the raw payload. */
function patientRows(detail: ITmpSessionDetail): [string, string][] {
  const phones = [
    jsonList(detail.patientMobilePhoneNumbers),
    jsonList(detail.patientPhoneNumbers),
  ]
    .filter(Boolean)
    .join(", ");

  const rows: [string, string][] = [
    [
      "Name",
      [
        detail.patientFirstName,
        detail.patientSecondName,
        detail.patientLastName,
      ]
        .filter(Boolean)
        .join(" "),
    ],
    ["Email", jsonList(detail.patientEmails)],
    ["Phone", phones],
    ["Date of birth", detail.patientBirthDate?.slice(0, 10) ?? ""],
    ["Gender", detail.patientGender ?? ""],
    ["Language", detail.patientLanguage ?? ""],
    ["Responsible HCP", hcpSummary(detail.responsibleHcp)],
    ["Prescribing HCP", hcpSummary(detail.prescribingHcp)],
  ];
  return rows.filter(([, value]) => value !== "");
}

/**
 * One TMP order (EN-5203): where it is now and what happens next, every step
 * it went through, then the data it carries. Offers the two operator actions:
 * re-run the provisioner now, or mint a fresh one-time completion link for
 * the hospital clinician. The link is shown once, here; the API does not
 * store it in a readable form.
 */
export function TmpSessionDrillDownModal({
  session,
  onClose,
}: ITmpSessionDrillDownModalProps) {
  const { showToast } = useToast();
  const { detail, isLoading, isError } = useTmpSessionDetail(
    session.telemonitoringId
  );
  const {
    events,
    isLoading: isLoadingEvents,
    isError: isEventsError,
  } = useTmpSessionEvents(session.telemonitoringId);
  const { retrySession, isRetrying } = useRetryTmpSession();
  const { issueOnboardingLink, isIssuingLink } = useIssueTmpOnboardingLink();
  const [link, setLink] = useState<ITmpOnboardingLink | null>(null);
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  // The row is a snapshot; prefer the detail once it has loaded.
  const current: ITmpSession = detail ?? session;
  const next = nextStep(current);
  const rows = detail ? patientRows(detail) : [];

  const handleRetry = async () => {
    try {
      const result = await retrySession(session.telemonitoringId);
      if (result.failed > 0 || result.errors.length > 0) {
        showToast(
          `Retry finished with errors: ${
            result.errors.join(" ") || `${result.failed} failed`
          }`,
          "warning",
          { autoClose: false }
        );
      } else {
        showToast(
          `Retry finished — ${result.patientsCreated} created, ${result.patientsLinked} linked, ${result.accepted} accepted.`,
          "success"
        );
      }
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error retrying this order."),
        "error"
      );
    }
  };

  const handleIssueLink = async () => {
    try {
      const issued = await issueOnboardingLink(session.telemonitoringId);
      setLink(issued);
      setCopied(false);
      showToast("A new completion link was generated.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error generating the completion link."
        ),
        "error"
      );
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard?.writeText(link.url);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <ModalContainer data-testid="tmp-session-drilldown">
      <ModalHeader>
        <ModalTitle>{patientDisplayName(current)}</ModalTitle>
        <ModalSubtitle>
          MRN {current.externalPatientId}
          {/* Absent, not null, until set: the API omits null properties. */}
          {current.wardId != null && ` · Ward ${current.wardId}`}
          {current.patientId != null && ` · pMp patient ${current.patientId}`}
        </ModalSubtitle>
      </ModalHeader>

      <StatusSummary data-testid="tmp-session-status">
        <StatusBadge
          status={provisioningStatusTone(current.provisioningStatus)}
        >
          {provisioningStatusLabel(current.provisioningStatus)}
        </StatusBadge>
        <NextStepText tone={next.tone}>{next.text}</NextStepText>
      </StatusSummary>

      {current.lastError && (
        <MessageErrorNotice>Last error: {current.lastError}</MessageErrorNotice>
      )}

      {link && (
        <LinkRow data-testid="tmp-onboarding-link">
          <LinkValue>{link.url}</LinkValue>
          <CopyLinkButton
            type="button"
            copied={copied}
            aria-label="Copy completion link"
            onClick={handleCopy}
          >
            {copied ? "Copied" : "Copy"}
          </CopyLinkButton>
        </LinkRow>
      )}
      {link && (
        <ModalSubtitle>
          Expires {getLongFormattedDate(link.expiresAt, "—")}. Hand this link to
          the hospital clinician; it works once and needs no login.
        </ModalSubtitle>
      )}

      <DetailGrid>
        <Field label="TMP status">{text(current.status)}</Field>
        <Field label="Responsible HCP">
          {text(current.responsibleHcpHealthProviderId)}
        </Field>
        <Field label="Received">
          {getLongFormattedDate(current.created, "—")}
        </Field>
        {current.missingFields.length > 0 && (
          <Field label="Missing">{fieldList(current.missingFields)}</Field>
        )}
        {current.nextAttemptAt && (
          <Field label="Next attempt">
            {getLongFormattedDate(current.nextAttemptAt, "—")}
          </Field>
        )}
      </DetailGrid>

      <DetailSectionTitle>History</DetailSectionTitle>
      {isLoadingEvents && <TableLoadingState />}
      {isEventsError && (
        <ErrorMessage>Failed to load the history of this order.</ErrorMessage>
      )}
      {!isLoadingEvents && !isEventsError && (
        <TmpSessionTimeline events={events} />
      )}

      {isLoading && <TableLoadingState />}
      {isError && (
        <ErrorMessage>
          Failed to load the order details. The summary above is from the list.
        </ErrorMessage>
      )}

      {detail && (
        <Collapsible data-testid="tmp-session-patient-data">
          <summary>Patient data from TMP</summary>
          {rows.length > 0 ? (
            <DetailGrid>
              {rows.map(([label, value]) => (
                <Field key={label} label={label}>
                  {value}
                </Field>
              ))}
            </DetailGrid>
          ) : (
            <ModalValue>TMP sent no patient details.</ModalValue>
          )}
        </Collapsible>
      )}

      <Collapsible data-testid="tmp-session-technical">
        <summary>Technical details</summary>
        <DetailGrid>
          <Field label="Telemonitoring ID">{current.telemonitoringId}</Field>
          <Field label="Service ID">{text(current.serviceId)}</Field>
          <Field label="Prescriber ID">{text(current.prescriberId)}</Field>
          <Field label="Provisioning status">
            {current.provisioningStatus}
          </Field>
          <Field label="Attempts in this step">{current.attempts}</Field>
          <Field label="Last attempt">
            {getLongFormattedDate(current.lastAttemptedAt, "—")}
          </Field>
        </DetailGrid>
      </Collapsible>

      <ModalFormActions>
        {current.canRetry && (
          <FilterButton
            variant="primary"
            onClick={handleRetry}
            disabled={isRetrying}
            data-testid="tmp-retry-session"
          >
            {isRetrying ? "Retrying…" : "Retry"}
          </FilterButton>
        )}
        {current.canIssueOnboardingLink && (
          <FilterButton
            variant="primary"
            onClick={handleIssueLink}
            disabled={isIssuingLink}
            data-testid="tmp-issue-link"
          >
            {isIssuingLink ? "Generating…" : "Generate completion link"}
          </FilterButton>
        )}
        <FilterButton variant="secondary" onClick={onClose}>
          Close
        </FilterButton>
      </ModalFormActions>
    </ModalContainer>
  );
}
